'use client'

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useAppSelector, useAppDispatch } from '@/src/redux/store'
import { selectAuth, refreshAuthUser } from '@/src/redux/slices/authSlice'
import { useGetWalletTransactionsQuery } from '@/src/api/rtkQuery/walletApi'
import {
    AllWalletTabType,
    WalletTransaction,
    DomainBalance,
    BrandBalance,
    FetchControl,
    MultiBrandWalletInfo,
    WalletTransactionsResponse
} from '@/src/types/wallet'
import { getCookie } from '@/src/utils/browser'
import { getCurrencyByLocale } from '@/src/utils/currency/formatCurrency'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'

const TRANSACTIONS_PAGE_SIZE = 10

export interface UseWalletOptions {
    initialWalletInfo?: MultiBrandWalletInfo | null
    initialTransactions?: WalletTransactionsResponse | null
    isMultiBrand?: boolean
}

// Extracts user ID from the userid cookie. Cookie format: "email%40domain.com%7C%7C%7C%7C5451900098735%7C". Decoded: "email@domain.com||||5451900098735|". Returns: "5451900098735"
const extractUserId = (userIdCookie: string | null): string | undefined => {
    if (!userIdCookie) return undefined

    try {
        // Decode URL-encoded cookie value
        const decoded = decodeURIComponent(userIdCookie)
        // Extract the numeric userId between pipe characters. Pattern: |...|...|...|userId|
        const idMatch = decoded.match(/\|(\d+)\|/)
        return idMatch?.[1]
    } catch {
        // Fallback: try without decoding if already decoded
        const idMatch = userIdCookie.match(/\|(\d+)\|/)
        return idMatch?.[1]
    }
}

export interface UseWalletReturn {
    // Auth state
    isLoggedIn: boolean
    userId: string | number | undefined

    // Wallet balance
    balance: number
    currency: string
    domainBalances: DomainBalance[]
    displayBalance: number
    displayCurrency: string
    hasMultipleDomainBalances: boolean

    brandBalances: BrandBalance[]
    mainBalance: number
    linkedBalance: number

    // Transactions
    transactions: WalletTransaction[]
    totalRecords: number
    hasMoreTransactions: boolean

    // Tab state
    activeTab: AllWalletTabType
    setActiveTab: (tab: AllWalletTabType) => void

    brandFilter: FetchControl
    setBrandFilter: (filter: FetchControl) => void

    // Pagination
    currentPage: number
    loadMoreTransactions: () => void

    // Loading states
    isLoadingBalance: boolean
    isLoadingTransactions: boolean
    isFetchingTransactions: boolean

    // Error states
    balanceError: string | null
    transactionsError: string | null

    // Actions
    refreshWallet: () => void

    // Brand
    isCleartrip: boolean
}

export const useWallet = (options?: UseWalletOptions): UseWalletReturn => {
    const { initialWalletInfo, initialTransactions, isMultiBrand = false } = options || {}
    const dispatch = useAppDispatch()
    const auth = useAppSelector(selectAuth)
    const isLoggedIn = auth.signedIn
    const isCleartripBrand = isCleartrip()
    const t = useTranslations('wallet')
    const { showInfo } = useThemedToast()
    const noMoreToastShownRef = useRef(false)

    // Get currency from domain only (not from utm_currency cookie)
    const currency = getCurrencyByLocale().currency

    const [isMounted, setIsMounted] = useState(false)

    // Extract userId directly from cookies (same pattern as authSlice.ts)
    const userid = useMemo(() => {
        if (!isMounted) return null
        return getCookie('userid')
    }, [isMounted])
    const userId = useMemo(() => {
        return userid ? extractUserId(userid) : undefined
    }, [userid])

    // Set mounted flag and refresh auth state on mount
    useEffect(() => {
        setIsMounted(true)
        dispatch(refreshAuthUser())
    }, [dispatch])

    // Tab state - Cleartrip defaults to 'ALL', Flyin defaults to 'EARNED'
    const defaultTab: AllWalletTabType = isCleartripBrand ? 'ALL' : 'EARNED'
    const [activeTab, setActiveTabState] = useState<AllWalletTabType>(defaultTab)
    const [currentPage, setCurrentPage] = useState(1)
    const [accumulatedTransactions, setAccumulatedTransactions] = useState<WalletTransaction[]>([])

    // Always start on Flyin filter to avoid mixing MAIN+LINKED on initial render.
    const defaultBrandFilter: FetchControl = 'MAIN'
    const [brandFilter, setBrandFilterState] = useState<FetchControl>(defaultBrandFilter)

    // Get domain currency for display preference (used to find matching balance from domainBalances)
    const domainCurrency = useMemo(() => {
        if (typeof window === 'undefined') {
            return getCurrencyByLocale().currency
        }
        return getCurrencyByLocale().currency
    }, [])

    // Skip queries if not logged in or userId not yet available (cookies read only after mount)
    const shouldSkip = !isLoggedIn || !userId

    // Skip transactions query if we have initial data for current params (page 1, default tab). Only fetch when params change or no initial data
    const shouldSkipTransactions =
        shouldSkip ||
        (!!initialTransactions &&
            currentPage === 1 &&
            activeTab === defaultTab &&
            brandFilter === defaultBrandFilter)

    const router = useRouter()

    const {
        data: transactionsData,
        isLoading: isLoadingTransactions,
        isFetching: isFetchingTransactions,
        error: transactionsErrorObj,
        refetch: refetchTransactions
    } = useGetWalletTransactionsQuery(
        {
            userId: userId as string | number,
            currency,
            type: activeTab,
            offset: currentPage,
            size: TRANSACTIONS_PAGE_SIZE,
            fetchControl: brandFilter
        },
        { skip: shouldSkipTransactions }
    )

    // Determine which data source to use. Balance is always from server (initialWalletInfo).
    const shouldUseInitialTransactions =
        !!initialTransactions &&
        currentPage === 1 &&
        activeTab === defaultTab &&
        brandFilter === defaultBrandFilter

    const effectiveWalletInfo = initialWalletInfo ?? null
    const effectiveTransactionsData = shouldUseInitialTransactions ? initialTransactions : transactionsData

    // Parse balance data
    const balance = effectiveWalletInfo?.status === 'SUCCESS' ? (effectiveWalletInfo.balance ?? 0) : 0
    const walletCurrency = effectiveWalletInfo?.status === 'SUCCESS' ? (effectiveWalletInfo.currency ?? currency) : currency
    const domainBalances = effectiveWalletInfo?.status === 'SUCCESS' ? (effectiveWalletInfo.domainBalances ?? []) : []

    const allBrandBalances = effectiveWalletInfo?.brandBalances ?? []
    const brandBalances = isMultiBrand
        ? allBrandBalances
        : allBrandBalances.filter(b => b.brandSource === 'MAIN')
    const mainBalance = effectiveWalletInfo?.mainBalance ?? 0
    const linkedBalance = isMultiBrand ? (effectiveWalletInfo?.linkedBalance ?? 0) : 0

    // Calculate display balance (prefer domain balance if available)
    const { displayBalance, displayCurrency } = useMemo(() => {
        const domainMatch = domainBalances.find(db => db.currency === domainCurrency)
        if (domainMatch) {
            return { displayBalance: domainMatch.balance, displayCurrency: domainMatch.currency }
        }
        return { displayBalance: balance, displayCurrency: walletCurrency }
    }, [balance, walletCurrency, domainBalances, domainCurrency])

    const hasMultipleDomainBalances = domainBalances.length > 1

    // Parse transactions - use accumulated for pagination
    const transactions = useMemo(() => {
        if (!effectiveTransactionsData?.transactions) {
            // If we have accumulated transactions, return them; otherwise return empty array
            return accumulatedTransactions.length > 0 ? accumulatedTransactions : []
        }
        // For page 1, return fresh data; for other pages, accumulate
        if (currentPage === 1) {
            return effectiveTransactionsData.transactions
        }
        // Combine accumulated with new data, avoiding duplicates
        const existingIds = new Set(accumulatedTransactions.map(t => t.transactionId))
        const newTransactions = effectiveTransactionsData.transactions.filter(t => !existingIds.has(t.transactionId))
        return [...accumulatedTransactions, ...newTransactions]
    }, [effectiveTransactionsData?.transactions, currentPage, accumulatedTransactions])

    const totalRecords = effectiveTransactionsData?.totalRecords ?? 0
    const hasMoreTransactions = transactions.length < totalRecords

    // Error messages (balance errors come from initialWalletInfo status)
    const balanceError =
        effectiveWalletInfo?.status === 'FAILURE'
            ? effectiveWalletInfo.message || 'Failed to fetch wallet info'
            : null

    const transactionsError =
        effectiveTransactionsData?.status === 'FAILURE'
            ? effectiveTransactionsData.message || 'Failed to fetch transactions'
            : transactionsErrorObj
              ? 'Failed to fetch transactions'
              : null

    // Handle tab change - reset pagination
    const setActiveTab = useCallback((tab: AllWalletTabType) => {
        setActiveTabState(tab)
        setCurrentPage(1)
        setAccumulatedTransactions([])
        noMoreToastShownRef.current = false
    }, [])

    const setBrandFilter = useCallback((filter: FetchControl) => {
        setBrandFilterState(filter)
        setCurrentPage(1)
        setAccumulatedTransactions([])
        noMoreToastShownRef.current = false
    }, [])

    // Cleartrip: toast when load more returns empty [] (matches repo pattern: useEffect + condition → toast)
    const isLoadMoreEmpty =
        isCleartripBrand &&
        currentPage > 1 &&
        !isFetchingTransactions &&
        (effectiveTransactionsData?.transactions?.length ?? 0) === 0 &&
        accumulatedTransactions.length > 0

    useEffect(() => {
        if (isLoadMoreEmpty && !noMoreToastShownRef.current) {
            noMoreToastShownRef.current = true
            showInfo(t('noMoreTransactions'))
        }
    }, [isLoadMoreEmpty, showInfo, t])

    // Load more transactions
    const loadMoreTransactions = useCallback(() => {
        if (isFetchingTransactions || transactions.length >= totalRecords) return
        // Save current transactions before loading more
        setAccumulatedTransactions(transactions)
        setCurrentPage(prev => prev + 1)
    }, [isFetchingTransactions, transactions, totalRecords])

    // Refresh wallet data (re-fetch from server for balance, refetch transactions on client)
    const refreshWallet = useCallback(() => {
        router.refresh()
        setCurrentPage(1)
        setAccumulatedTransactions([])
        noMoreToastShownRef.current = false
        refetchTransactions()
    }, [router, refetchTransactions])

    // Loading states - balance is server-only so never loading on client; transactions loading when not using initial data
    const effectiveIsLoadingBalance = false
    const effectiveIsLoadingTransactions =
        !isMounted || shouldUseInitialTransactions ? false : isLoadingTransactions
    const effectiveIsFetchingTransactions =
        !isMounted || shouldUseInitialTransactions ? false : isFetchingTransactions

    return {
        isLoggedIn,
        userId,
        balance,
        currency: walletCurrency,
        domainBalances,
        displayBalance,
        displayCurrency,
        hasMultipleDomainBalances,
        brandBalances,
        mainBalance,
        linkedBalance,
        transactions,
        totalRecords,
        hasMoreTransactions,
        activeTab,
        setActiveTab,
        brandFilter,
        setBrandFilter,
        currentPage,
        loadMoreTransactions,
        isLoadingBalance: effectiveIsLoadingBalance,
        isLoadingTransactions: effectiveIsLoadingTransactions,
        isFetchingTransactions: effectiveIsFetchingTransactions,
        balanceError,
        transactionsError,
        refreshWallet,
        isCleartrip: isCleartripBrand
    }
}

export default useWallet
